import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
} from "./utils";
import type {
  WrappedCashifyFunction,
  WrappedCashifyPageableQuery,
  CommonResponse,
  GenericResponse,
  Sortable,
} from "./utils";
import type { SupportedLocale } from "components/supported-language";

export interface MessagingTemplateService {
  listMessagingTemplate: ReturnType<typeof listMessagingTemplate>;
  postMessagingTemplate: ReturnType<typeof postMessagingTemplate>;
  getMessagingTemplateDetail: ReturnType<typeof getMessagingTemplateDetail>;
  putMessagingTemplateDetail: ReturnType<typeof putMessagingTemplateDetail>;
  deleteMessagingTemplateDetail: ReturnType<
    typeof deleteMessagingTemplateDetail
  >;
  putLocalizedTemplateMessage: ReturnType<typeof putLocalizedTemplateMessage>;
  deleteLocalizedTemplateMessage: ReturnType<
    typeof deleteLocalizedTemplateMessage
  >;
}

// Messaging Template
export interface TemplateSearchCriteria extends Sortable {
  id?: number;
  name?: string;
  locale?: SupportedLocale;
}

export interface TemplateDTO {
  id: number;
  name: string;
}

export interface TemplateMessageDetailsDTO extends TemplateDTO {
  note?: string;
  description?: string;
  localizedTemplateMessages?: {
    [key in SupportedLocale]: LocalizedTemplateDTO;
  };
}

export interface LocalizedTemplateDTO {
  id: number;
  locale: SupportedLocale;
  title?: string;
  content?: string;
  createTime?: string;
  createAdmin?: string;
  lastUpdateTime?: string;
  lastUpdateAdmin?: string;
}

export interface TemplateCreateRequest {
  name: string;
  note?: string;
  description?: string;
  localizedTemplateMessages?: {
    [key in SupportedLocale]: LocalizedTemplateDTO;
  };
}

export interface TemplateUpdateRequest {
  id: number;
  detail: {
    name?: string;
    note?: string;
    description?: string;
    localizedTemplateMessages?: {
      [key in SupportedLocale]: LocalizedTemplateDTO;
    };
  };
}

export interface LocalizedTemplateDeleteRequest {
  id: number;
  locale: SupportedLocale;
}

export interface LocalizedTemplateUpdateRequest
  extends LocalizedTemplateDeleteRequest {
  detail: {
    title?: string;
    content?: string;
  };
}

export const listMessagingTemplate: WrappedCashifyPageableQuery<
  TemplateSearchCriteria,
  TemplateMessageDetailsDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/messaging/template`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const postMessagingTemplate: WrappedCashifyFunction<
  TemplateCreateRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async (request) => {
    let url = `${urlPrefix}/api/v1/messaging/template`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getMessagingTemplateDetail: WrappedCashifyFunction<
  { id: number },
  GenericResponse<TemplateMessageDetailsDTO>
> = ({ urlPrefix, options }) => {
  return async ({ id }) => {
    let url = `${urlPrefix}/api/v1/messaging/template/${id}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putMessagingTemplateDetail: WrappedCashifyFunction<
  TemplateUpdateRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, detail }) => {
    let url = `${urlPrefix}/api/v1/messaging/template/${id}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteMessagingTemplateDetail: WrappedCashifyFunction<
  { id: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id }) => {
    let url = `${urlPrefix}/api/v1/messaging/template/${id}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putLocalizedTemplateMessage: WrappedCashifyFunction<
  LocalizedTemplateUpdateRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, locale, detail }) => {
    let url = `${urlPrefix}/api/v1/messaging/template/${id}/${locale}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteLocalizedTemplateMessage: WrappedCashifyFunction<
  LocalizedTemplateDeleteRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, locale }) => {
    let url = `${urlPrefix}/api/v1/messaging/template/${id}/${locale}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
